import type { AISettings, AIClient, FallbackConfig } from '$lib/types/ai';
import { OpenAIStreamingClient } from './OpenAIStreamingClient';
import { AnthropicStreamingClient } from './AnthropicStreamingClient';
import { AIClientFactory } from '../AIClientFactory';

/**
 * Factory for creating streaming AI clients with fallback support
 */
export class StreamingClientFactory {
  /**
   * Create a streaming client with fallback configuration
   * Falls back to non-streaming clients for providers that don't support streaming
   */
  static createStreamingClient(
    primarySettings: AISettings,
    fallbackConfig?: Partial<FallbackConfig>
  ): AIClient {
    
    // Default fallback configuration
    const config: FallbackConfig = {
      primary: primarySettings,
      secondary: fallbackConfig?.secondary,
      tertiary: fallbackConfig?.tertiary,
      maxRetries: fallbackConfig?.maxRetries ?? 3,
      retryDelay: fallbackConfig?.retryDelay ?? 1000,
      timeout: fallbackConfig?.timeout ?? 30000,
      exponentialBackoff: fallbackConfig?.exponentialBackoff ?? true
    };

    // Create streaming client based on primary provider
    switch (primarySettings.provider) {
      case 'openai':
        if (!primarySettings.openaiApiKey) {
          throw new Error('OpenAI API key not configured');
        }
        return new OpenAIStreamingClient(
          primarySettings.openaiApiKey,
          primarySettings.selectedModel,
          primarySettings.maxTokens,
          config,
          primarySettings
        );
      
      case 'anthropic':
        if (!primarySettings.anthropicApiKey) {
          throw new Error('Anthropic API key not configured');
        }
        return new AnthropicStreamingClient(
          primarySettings.anthropicApiKey,
          primarySettings.selectedModel,
          primarySettings.maxTokens,
          config,
          primarySettings
        );
      
      // For providers that don't support streaming yet, fall back to regular client
      case 'google':
      case 'upstage':
      case 'openrouter':
      default:
        console.warn(`[StreamingFactory] Provider ${primarySettings.provider} doesn't support streaming, using standard client`);
        return AIClientFactory.createClient(primarySettings);
    }
  }

  /**
   * Check if a provider supports streaming
   */
  static supportsStreaming(provider: string): boolean {
    return ['openai', 'anthropic'].includes(provider);
  }

  /**
   * Create a fallback configuration with multiple providers
   */
  static createFallbackConfig(
    primary: AISettings,
    secondary?: AISettings,
    tertiary?: AISettings,
    options?: {
      maxRetries?: number;
      retryDelay?: number;
      timeout?: number;
      exponentialBackoff?: boolean;
    }
  ): FallbackConfig {
    return {
      primary,
      secondary,
      tertiary,
      maxRetries: options?.maxRetries ?? 3,
      retryDelay: options?.retryDelay ?? 1000,
      timeout: options?.timeout ?? 30000,
      exponentialBackoff: options?.exponentialBackoff ?? true
    };
  }

  /**
   * Create a default fallback configuration using the same provider with different models
   */
  static createModelFallbackConfig(
    settings: AISettings,
    fallbackModels: string[]
  ): FallbackConfig {
    const baseConfig = {
      maxRetries: 3,
      retryDelay: 1000,
      timeout: 30000,
      exponentialBackoff: true
    };

    const primary = settings;
    let secondary: AISettings | undefined;
    let tertiary: AISettings | undefined;

    if (fallbackModels.length > 0) {
      secondary = {
        ...settings,
        selectedModel: fallbackModels[0]
      };
    }

    if (fallbackModels.length > 1) {
      tertiary = {
        ...settings,
        selectedModel: fallbackModels[1]
      };
    }

    return {
      ...baseConfig,
      primary,
      secondary,
      tertiary
    };
  }
}